import React from 'react'
import { Helmet } from 'react-helmet'
import { NotificationHost } from 'react-notifications'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'
import { FetchStatus } from 'mobx-document'
import { useHistory } from '~/history'
import { projectStore } from '~/stores'
import { ChatUri } from '~/stores/chat'
import ChatChatContainer from '~/ui/app/chat/ChatContainer'
import ChatMiniPanel from '~/ui/app/chat/ChatMiniPanel'
import { ChatContainer, useChatService } from '~/ui/app/ChatContainer'
import ErrorBoundary from '~/ui/app/ErrorBoundary'
import { NavigationTabBar } from '~/ui/app/navigation'
import { Breadcrumb } from '~/ui/app/navigation/types'
import BrowserNotificationsBar from '~/ui/app/notifications/BrowserNotificationsBar'
import { memo, observer } from '~/ui/component'
import { HBox, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { screenMinWidths } from '~/ui/styling/layout'
import AppHeader from './AppHeader'
import AppLayoutContext, { AppLayoutConfig } from './AppLayoutContext'
import AppSideBar from './AppSideBar'

const CHAT_PANEL_COLLAPSED_KEY = 'chat-panel-collapsed'

export interface Props {
  children?: React.ReactNode
}

const AppLayout = observer('AppLayout', (props: Props) => {

  const {children} = props

  const history = useHistory()

  const [title, setTitle] = React.useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([])
  const [fetchStatus, setFetchStatus] = React.useState<FetchStatus | null>(null)
  const [ActionsComponent, setActionsComponent] = React.useState<React.ComponentType<{}>>()

  const configure = React.useCallback((config: AppLayoutConfig) => {
    setTitle(config.title)
    setBreadcrumbs(config.breadcrumbs)
    setFetchStatus(config.fetchStatus)
    // As the Actions Component itself may be a function, and the state hook will execute a function instead of holding on to it,
    // we need to wrap this in a function itself.
    setActionsComponent(() => config.ActionsComponent)
  }, [])

  const isDesktop    = useMediaQuery({minWidth: screenMinWidths.desktop})
  const isWidescreen = useMediaQuery({minWidth: screenMinWidths.widescreen})

  const tabs = projectStore.tabs
  const hasChatTab = React.useMemo(
    () => tabs.find(it => it.link.href.startsWith('//chat')),
    [tabs],
  )

  const collapsed = hasChatTab ? !isWidescreen : !isDesktop

  const [chatPanelCollapsed, setChatPanelCollapsed] = React.useState<boolean>(
    () => localStorage.getItem(CHAT_PANEL_COLLAPSED_KEY) === 'true',
  )

  const toggleChatPanel = React.useCallback(() => {
    setChatPanelCollapsed(prev => {
      const next = !prev
      localStorage.setItem(CHAT_PANEL_COLLAPSED_KEY, String(next))
      return next
    })
  }, [])

  React.useEffect(() => {
    if (!collapsed && history.location.pathname.startsWith('/chat')) {
      history.replace(`/${history.location.hash}`)
    }
  }, [history, collapsed])

  const context = React.useMemo(() => ({
    title,
    breadcrumbs,
    fetchStatus,
    ActionsComponent,
    collapsed,
    configure,
  }), [title, breadcrumbs, fetchStatus, ActionsComponent, collapsed, configure])

  React.useEffect(() => {
    if (title == null || history.stack.length === 0) { return }
    history.stack[history.index].title = title
    history.save()
  }, [title, history])

  const project = projectStore.project

  const location = useLocation()

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <AppLayoutContext.Provider value={context}>
        {title != null && <Helmet title={title}/>}
        <ChatContainer>
          <NotificationHost
            className={$.notificationHost}
            padding={layout.padding.inline.l}
          />
          {renderContent()}
        </ChatContainer>
      </AppLayoutContext.Provider>
    )
  }

  function renderContent() {
    return (
      <VBox classNames={$.content} flex='grow'>
        {project == null ? null : collapsed ? (
          <>
            <AppHeader/>
            {location.pathname === '/chat' ? (
              <ChatWrapper chatPanelCollapsed={false} onToggleChatPanel={toggleChatPanel}/>
            ) : (
              <ErrorBoundary>
                {children}
              </ErrorBoundary>
            )}
            <NavigationTabBar/>

          </>
        ) : (
          <HBox flex align='stretch'>
            <AppSideBar/>
            <VBox flex>
              <AppHeader/>
              <ErrorBoundary>
                {children}
              </ErrorBoundary>
            </VBox>
            {hasChatTab && (
              <VBox classNames={[$.chat, {miniPanel: chatPanelCollapsed}]}>
                <ChatWrapper
                  chatPanelCollapsed={chatPanelCollapsed}
                  onToggleChatPanel={toggleChatPanel}
                />
              </VBox>
            )}
          </HBox>
        )}
      </VBox>
    )
  }

  return render()

})

export default AppLayout

interface ChatWrapperProps {
  chatPanelCollapsed:  boolean
  onToggleChatPanel:   () => void
}

const ChatWrapper = memo('ChatWrapper', (props: ChatWrapperProps) => {

  const {chatPanelCollapsed, onToggleChatPanel} = props

  // Delay mounting the full panel until after the expand transition finishes,
  // so the textarea scrollHeight is measured at full panel width.
  const [fullPanelReady, setFullPanelReady] = React.useState(!chatPanelCollapsed)

  React.useEffect(() => {
    if (chatPanelCollapsed) {
      setFullPanelReady(false)
    } else {
      const timer = setTimeout(() => setFullPanelReady(true), chatPanelTransitionMs)
      return () => clearTimeout(timer)
    }
  }, [chatPanelCollapsed])

  const service = useChatService()
  const history = useHistory()
  const location = useLocation()

  React.useEffect(() => {
    const chatUri = location.hash?.slice(1) as ChatUri ?? null
    if (chatUri == null) { return }

    const [type, id] = chatUri.split(':')
    if (type !== 'private') { return }

    service?.ensurePrivateChat(id)
  }, [location.hash, service])

  React.useEffect(() => {
    if (service == null) { return }

    const chatUri = location.hash == null || location.hash === '' ? null : location.hash.slice(1) as ChatUri
    return service.syncWithAppNavigation(chatUri, uri => {
      history.replace(uri == null ? '#' : `#${uri}`)
    })
  }, [history, location.hash, service])

  // Auto-expand the panel when a notification (or any navigation) targets a chat URI.
  const chatPanelCollapsedRef = React.useRef(chatPanelCollapsed)
  chatPanelCollapsedRef.current = chatPanelCollapsed
  const onToggleChatPanelRef = React.useRef(onToggleChatPanel)
  onToggleChatPanelRef.current = onToggleChatPanel

  React.useEffect(() => {
    const chatUri = location.hash == null || location.hash === '' ? null : location.hash.slice(1)
    if (chatUri != null && chatPanelCollapsedRef.current) {
      onToggleChatPanelRef.current()
    }
  }, [location.hash])

  //------
  // Rendering

  function render() {
    if (service == null) {return null}

    if (chatPanelCollapsed || !fullPanelReady) {
      return (
        <ErrorBoundary>
          <ChatMiniPanel
            service={service}
            onExpand={onToggleChatPanel}
          />
        </ErrorBoundary>
      )
    }

    return (
      <ErrorBoundary>
        <ChatChatContainer
          service={service}
          subheader={<BrowserNotificationsBar/>}
          onCollapse={onToggleChatPanel}
        />
      </ErrorBoundary>
    )
  }

  return render()

})

export const chatPanelWidth        = layout.columnWidth + layout.padding.inline.m
export const miniPanelWidth        = 64
export const chatPanelTransitionMs = 200

const useStyles = createUseStyles(theme => ({
  notificationHost: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: layout.z.notification,
    ...layout.breakpoint({minWidth: screenMinWidths.tablet})({
      left: 'auto',
      width: layout.columnWidth,
    }),
  },

  content: {
    position: 'relative',
  },

  chat: {
    width:      layout.columnWidth,
    borderLeft: [2, 'solid', theme.border.dimmer],
    transition: `width ${chatPanelTransitionMs}ms ease`,
    overflow:   'hidden',

    '&.miniPanel': {
      width: miniPanelWidth,
    },
  },

  BrowserNotificationsBar: {
    padding:    [layout.padding.inline.xs, layout.padding.inline.m],
    background: theme.bg.normal,
  },
}))